import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { notify } from "@/components/common/Notification";
import { renderStatusBadge } from "@/lib/renderStatusBadge";
import {
  DataOperationsService,
  ImportManagementService,
  SchemaService,
} from "@/services/APIs";
import { ImportRecord } from "@/utils/types/importManagement";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Alert, Card } from "antd";
import { AlertCircle, Play, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ColumnMappingForm from "./components/ColumnMappingForm";
import ImportSummary from "./components/ImportSummary";
import PendingUI from "./components/PendingUI";
import ProcessingUI from "./components/ProcessingUI";

const ImportDetailPage = () => {
  const { importId } = useParams<{ importId: string }>();

  // State for column mapping
  const [isMappingMode, setIsMappingMode] = useState(false);
  const [isMappingSaved, setIsMappingSaved] = useState(false);
  const [isRunningImport, setIsRunningImport] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingMapping, setIsUpdatingMapping] = useState(false);

  const handleRunImport = async (importId: string) => {
    setIsRunningImport(true);
    const response = await DataOperationsService.runImport(importId);
    if (response) {
      refetchImportDetail();
    }
    setIsRunningImport(false);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchImportDetail();
    setIsRefreshing(false);
  };

  // Use React Query for data fetching
  const {
    data: importDetailResponse,
    isLoading: importDetailLoading,
    refetch: refetchImportDetail,
  } = useQuery({
    queryKey: ["imports", "detail", importId],
    queryFn: () => ImportManagementService.getImportDetail(importId ?? ""),
    enabled: !!importId,
  });

  console.log(importDetailResponse?.data, "importDetailResponse");

  // Use API response data
  const importDetail: ImportRecord | undefined | any =
    importDetailResponse?.data ?? undefined;

  // Use React Query for column grid data
  const { data: columnGridData } = useQuery({
    queryKey: ["schema", "grid", importDetail?.schema_db_name],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          importDetail?.schema_db_name ?? ""
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!importDetail?.schema_db_name,
  });

  // Get database column names for mapping (filter out system fields)
  const dbColumns =
    columnGridData?.grid
      ?.filter((field: any) => {
        const systemFields = [
          "id",
          "is_active",
          "created_on",
          "updated_on",
          "created_by",
          "updated_by",
          "tenant",
          "created_by_id",
          "updated_by_id",
          "tenant_id",
        ];
        return !systemFields.includes(field.field_name.toLowerCase());
      })
      ?.map((field: any) => ({
        label: field.field_title,
        value: field.field_name.toLowerCase(),
      })) ?? [];

  // Get file columns from existing mapping
  const fileColumns = Array.isArray(importDetail?.column_mapping)
    ? importDetail.column_mapping.map((m: any) => m.file_column_name)
    : [];

  // Initialize form with existing mapping
  useEffect(() => {
    if (Array.isArray(importDetail?.column_mapping)) {
      setIsMappingSaved(
        importDetail.column_mapping.filter((m: any) => m.db_column_name)
          .length > 0
      );
    } else {
      setIsMappingSaved(false);
    }
  }, [importDetail]);

  // Handle run import
  const handleRunImportAction = async () => {
    if (!importId) {
      notify.error("No Import ID", "Please check the import configuration.");
      return;
    }

    await handleRunImport(importId);
  };

  // Handle download error file
  const handleDownloadErrorFile = async () => {
    if (importDetail?.error_file) {
      const response = await DataOperationsService.downloadErrorFile(
        importId ?? ""
      );
      if (response) {
        notify.info(
          "Download Error File",
          "Error file downloaded successfully."
        );
      }
    } else {
      notify.warning(
        "No Error File",
        "No error file available for this import."
      );
    }
  };

  // Check if any action is in progress
  const isAnyActionInProgress =
    isRunningImport ?? isRefreshing ?? isUpdatingMapping ?? importDetailLoading;

  if (importDetailLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!importDetail) {
    return (
      <Alert
        type="error"
        message="Import Not Found"
        description="The requested import record could not be found."
        showIcon
        icon={<AlertCircle />}
      />
    );
  }

  const isPending = importDetail?.status === "pending";
  const isProcessing = importDetail.status === "Processing";
  const mappedColumns = Array.isArray(importDetail.column_mapping)
    ? importDetail.column_mapping.filter((m: any) => m.db_column_name).length
    : 0;
  const totalColumns = dbColumns.length;
  const fileNameParts = importDetail?.file_name?.split("/");
  const actualFileName = fileNameParts?.[fileNameParts?.length - 1];

  const renderStatusUI = () => {
    if (isPending) {
      return (
        <>
          <PendingUI
            importDetail={importDetail}
            dbColumns={dbColumns}
            mappedColumns={mappedColumns}
            totalColumns={totalColumns}
            onConfigureMapping={() => setIsMappingMode(true)}
            disabled={isAnyActionInProgress}
          />
          {isMappingMode && (
            <ColumnMappingForm
              columnGridData={columnGridData}
              dbColumns={dbColumns}
              fileColumns={fileColumns}
              importId={importId ?? ""}
              onMappingUpdate={refetchImportDetail}
              onRunImport={handleRunImportAction}
              isMappingSaved={isMappingSaved}
              setIsMappingSaved={setIsMappingSaved}
              disabled={isAnyActionInProgress}
              isUpdatingMapping={isUpdatingMapping}
              isRunningImport={isRunningImport}
              setIsUpdatingMapping={setIsUpdatingMapping}
            />
          )}
        </>
      );
    }

    if (isProcessing) {
      return <ProcessingUI importDetail={importDetail} />;
    }

    if (
      importDetail?.status === "Success" ||
      importDetail?.status === "Failed"
    ) {
      return (
        <ImportSummary
          importDetail={importDetail}
          onDownloadErrorFile={handleDownloadErrorFile}
        />
      );
    }

    return null;
  };

  const renderExtra = () => {
    const extra = [];
    if (importDetail?.status === "pending") {
      extra.push(
        isMappingSaved && (
          <CustomButton
            key="run-import"
            icon={<Play size={14} />}
            onClick={handleRunImportAction}
            loading={isRunningImport}
            disabled={isAnyActionInProgress}
            title="Execute Import Process"
          >
            Run Import
          </CustomButton>
        ),
        <CustomButton
          key="refresh"
          icon={<RefreshCw size={14} />}
          onClick={handleRefresh}
          loading={isRefreshing}
          disabled={isAnyActionInProgress}
          title="Refresh Import Status"
        >
          Refresh
        </CustomButton>
      );
    }

    return extra;
  };

  console.log(importDetail, "importDetail");

  return (
    <Card
      title={
        <PageHeader
          title={`${importDetail?.schema_name} - ${actualFileName}`}
          subTitle={renderStatusBadge(importDetail?.status)}
          onBack={() => window.history.back()}
          extra={[renderExtra()]}
        />
      }
    >
      {/* Status-specific UI */}
      <div className="space-y-4">{renderStatusUI()}</div>
    </Card>
  );
};

export default ImportDetailPage;
